import { ID, intboolean } from "app/types/app-types"
import LayerGroupType from "shape-entities/types/layerGroup";
import Shape from "shape-entities/types/shape";
import { LangPrefixType } from "trans";
import { ClipMaskObjectType } from "shape-edit/forms/edit-clip-mask-modal";
import { AITemplatType } from "products/types";
import { LevelsModeType } from "template-edit/components/save-template-form";

export interface Project {
    id: ID
    name: string
    contact_email: string
    who_are_you: string
    active_templates_count?: number
    templates_count?: number
    show_newsbox?: number
    show_first_steps?: number
    show_popup?: number
    user?: UserType
    outputLangPrefix: LangPrefixType
    shop_name: string
    shop_id: number
    input_format: string
    output_format: string
    is_input_csv: intboolean
    is_output_csv: intboolean
    active: intboolean
    deleting: intboolean
    is_god?: intboolean
    godAllProjects?: [{ value: number, text: string }]
    products_count?: number
    e_img_url_input: string
    e_img_url: string
    e_alt_img_url: string
    e_alt_img_url_input: string
    e_first_alt_img_url: string
    e_item_id: string
    e_url: string
    e_name: string
    e_price_vat: string
    e_ean: string
    e_productno: string
    enabled_at: string
    disabled_at: string
    input_feed: string
    creator_id: number
    process_apply_started_at: string
    process_apply_finished_at: string
    mergado_element_regex: string
    mergado_max_payload_size: number
    mergado_xml_equivalent: string
}

export type AnyPoint = {
    x: number
    y: number
}

export type FloodFillPoint = {
    x: number
    y: number
    fuzz: number
}

export type OpaquePaintArguments = {
    target: string
    fill: string
    fuzz: number
    invert?: boolean
    channel?: number
}

export interface EffectsType {
    name: string
    project_id: ID
    brightness: number
    contrast: number
    gamma: number
    sharpen_radius: number
    sharpen_sigma: number
    auto_gamma: boolean
    auto_level: boolean
    distort_method: number
    distort_arguments: string
    flood_fill_points: string
    opaque_paint_arguments: string
}

export interface ProductModelType {
    id: ID
    template_id: ID
    project_id: ID
    image_link: string
    serialized_data: string
    created_at: string
    updated_at: string
    rendered_at: string
    rendered_by: string
    should_rerender?: intboolean
    rendered_hash?: string
    rule_applied_at: string
    elements?: ProductElementType
    template?: Template
    from_api: intboolean
}

export interface ProductGalleryType {
    id: ID
    template_id: ID
    image_link: string
    should_rerender: intboolean
    rendered_hash: string
    elements?: ProductElementType
    template?: Template
}

export interface ProductElementType {
    id: number
    project_id: ID
    product_id: ID
    item_id: string | number
    url: string
    cpc: number | null
    name: string
    img_url: string
    price_vat: number | string
    delivery_date: string
    manufacturer: string
    categorytext: string
    ean: number | string
    productno: string
    accessory: string
    gift: string
    itemgroup_id: string
}

export const emptyEffects: EffectsType = {
    name: null,
    project_id: null,
    brightness: null,
    contrast: null,
    gamma: null,
    sharpen_radius: null,
    sharpen_sigma: null,
    auto_gamma: null,
    auto_level: null,
    distort_method: null,
    distort_arguments: null,
    flood_fill_points: null,
    opaque_paint_arguments: null,
}

export type SensitivityType = 'LOW' | 'MEDIUM' | 'HIGH'
export type EnlargeByType = "bigger" | "smaller" | "width" | "height" | "fit" | "none"
export type BackgroundType = "color" | "gradient" | "random_colors" | "random_gradients" | "dominant_color" | "dominant_color_2" | "dominant_color_3"

export type Template = {
    id: ID
    project_id: ID
    queries: string[]
    size: Size
    size_id: ID
    is_keep_size: intboolean
    priority: number
    enlarge: intboolean
    enlarge_by: EnlargeByType
    flip: intboolean
    flop: intboolean
    effect_id: ID
    gradient_id: ID
    effects: EffectsType
    gradient: Gradient
    gradients: Gradient[]
    is_collage: intboolean
    collage_all_available_only: intboolean
    collage_layout_id: ID
    collage_layout?: CollageLayoutType
    rotation: number
    name: string
    type: AITemplatType
    background: string
    background_colors: string
    background_gradient_ids: string
    background_type: BackgroundType
    alpha: number
    crop: intboolean
    translate_x: number
    translate_y: number
    overscan: number
    outpaint_over: intboolean
    max_image_width: number
    max_image_height: number
    shapes: Shape[]
    groups: LayerGroupType[]
    active: intboolean
    active_from: string | null
    active_to: string | null
    is_anim_gif: intboolean
    is_autocrop: intboolean | null
    frames_delay: number
    loops_delay: number
    watermark_group_id: number
    watermark_methods?: string[]
    inpainting_group_id: number
    effect_sensitivity?: SensitivityType
    min_detection_accuracy: number
    has_reflection: intboolean
    reflection_angle: number
    reflection_offset: number
    reflection_opacity: number
    shadow_color: string
    shadow_opacity: number
    shadow_sigma: number
    shadow_xoffset: number
    shadow_yoffset: number
    is_dynamic?: boolean
    products_count?: number
    is_aspect_size_ratio?: boolean
    created_at?: string
    updated_at?: string
    ai_params_enabled?: intboolean
    visual_similarity_enabled?: intboolean
    visual_similarity_n?: number
    visual_similarity_threshold?: number
    visual_similarity_templateids?: ID[]
    random_product_id?: number
}

export type FormNameType = 'templateForm' | 'altTemplateForm'

export type AlternativeTemplatesLevels = {
    id: ID
    alternative_template_id: ID
    template_id: ID
    project_id: ID
    priority: number
    levels: string
    levels_mode: LevelsModeType
    updated_at: string
    created_at: string
}

export type AlternativeTemplate = {
    id: ID
    project_id: ID
    queries: string[]
    size: Size
    size_id: ID
    is_keep_size: intboolean
    priority: number
    enlarge: intboolean
    enlarge_by: EnlargeByType
    flip: intboolean
    flop: intboolean
    effect_id: ID
    gradient_id: ID
    effects: EffectsType
    gradient: Gradient
    gradients: Gradient[]
    is_collage: intboolean
    collage_all_available_only: intboolean
    collage_layout_id: ID
    collage_layout?: CollageLayoutType
    rotation: number
    name: string
    type: AITemplatType
    background: string
    background_colors: string
    background_gradient_ids: string
    background_type: BackgroundType
    alpha: number
    crop: intboolean
    translate_x: number
    translate_y: number
    overscan: number
    outpaint_over: intboolean
    max_image_width: number
    max_image_height: number
    shapes: Shape[]
    groups: LayerGroupType[]
    active: intboolean
    active_from: string | null
    active_to: string | null
    is_anim_gif: intboolean
    is_autocrop: intboolean
    frames_delay: number
    loops_delay: number
    watermark_group_id: number
    watermark_methods?: string[]
    inpainting_group_id: number
    effect_sensitivity?: SensitivityType
    min_detection_accuracy: number
    has_reflection: intboolean
    reflection_angle: number
    reflection_offset: number
    reflection_opacity: number
    shadow_color: string
    shadow_opacity: number
    shadow_sigma: number
    shadow_xoffset: number
    shadow_yoffset: number
    is_dynamic?: boolean
    products_count?: number
    is_aspect_size_ratio?: boolean
    created_at?: string
    updated_at?: string
    ai_params_enabled?: intboolean
    visual_similarity_enabled?: intboolean
    visual_similarity_n?: number
    visual_similarity_threshold?: number
    visual_similarity_templateids?: ID[]
    random_product_id?: number
}

export const templateCheckFields = [
    'queries',
    'size',
    'size_id',
    'priority',
    'enlarge',
    'enlarge_by',
    'flip',
    'flop',
    'effect_id',
    'gradient_id',
    'is_collage',
    'type',
    'effect_sensitivity',
    'watermark_group_id',
    'collage_all_available_only',
    'collage_layout_id',
    'rotation',
    'name',
    'background',
    'crop',
    'translate_x',
    'translate_y',
    'overscan',
    'active_from',
    'active_to',
    'is_anim_gif',
    'is_autocrop',
    'frames_delay',
    'loops_delay',
    'min_detection_accuracy',
    'has_reflection',
    'reflection_angle',
    'reflection_offset',
    'reflection_opacity',
    'shadow_color',
    'shadow_opacity',
    'shadow_sigma',
    'shadow_xoffset',
    'shadow_yoffset',
    'is_aspect_size_ratio',
]

export const shapeCheckFields = [
    'type',
    'is_hidden',
    'is_locked',
    'is_remove_background',
    'x',
    'y',
    'name',
    'index',
    'priority',
    'color',
    'width',
    'height',
    'angle',
    'is_flip',
    'is_flop',
    'text_content',
    'text_direction',
    'text_orientation',
    'text_fontsize',
    'text_fontfamily',
    'text_color',
    'text_background_color',
    'text_background_type',
    'image_xml_element',
    'text_xml_element',
    'text_xml_element_formatter',
    'rating_xml_element',
    'rating_count_xml_element',
    'rating_from_depots',
    'text_overflow',
    'text_decoration',
    'text_decoration_color',
    'text_align',
    'text_valign',
    'text_padding',
    'external_image_prompt',
    'dash_length',
    'dash_density',
    'stroke_width',
    'stroke_color',
    'line_cap',
    'line_join',
    'set_id',
    'set_file_id',
    'image_file_id',
    'custom_set_id',
    'custom_set_file_id',
    'gradient_id',
    'corner_radius',
    'shadow_color',
    'shadow_opacity',
    'shadow_sigma',
    'shadow_xoffset',
    'shadow_yoffset',
    'brush_strokes',
    'clip_mask',
    'has_reflection',
    'reflection_angle',
    'reflection_offset',
    'reflection_opacity',
    'reflection_flip',
]

export interface Query {
    id: ID
    project_id: ID
    name: string
    read_only?: boolean
    search_output?: boolean
    product_count: number
    query?: string
    tag?: string
}

export interface Size {
    id: ID
    project_id: ID
    name: string
    width: number
    height: number
    templates?: string[]
    created_at?: string | Date
    updated_at?: string | Date
}

export interface StopPoint {
    offset: number
    color: string
}

export interface Gradient {
    id?: ID
    project_id?: ID
    type: string
    x1: number
    y1: number
    x2: number
    y2: number
    stop_points: StopPoint[]
    radius?: number
    opacity?: number
}

export interface Palette {
    id: ID
    name: string
    colors: string[]
}

export interface CustomPalette {
    id: ID
    project_id: ID
    name: string
    custom_set_file_id: ID
    colors: string[]
    image_url?: string,
}

export interface SettingsFormType {
    id: ID
    project_id: ID
    output_image_format: string
    output_image_compression: number
    auto_compression_to_size: number
    skip_refresh_images: number
    ask_before_leave: intboolean
    group_by_image: intboolean
    disable_mailgun_notifications: intboolean
    auto_template_priority: intboolean
    products_per_page?: number
    products_per_page_on_list: number
    created_at?: string | Date
    updated_at?: string | Date
    hide_by_status_code: string
    hide_by_has_watermark: boolean
    hide_by_wrong_placement: boolean
    hide_by_low_quality: boolean
    hide_by_seo_large_file: boolean
    hide_by_is_empty: boolean
    hide_by_no_image: boolean
    hide_by_content_length_gt: number
    hide_by_content_length_lt: number
    short_url_with_extension: number
    similarity_ids_element: number
    similarity_image_urls_element: number
    force_remove_alpha: intboolean
    settings_order: string
    disable_helper: intboolean
    disable_wheelzoom: intboolean
    disable_show_overlap: intboolean
    palette_id: ID | number
    custom_palette_id: ID | number
}

export type CustomPalettesFormType = {
    palettes: CustomPalette[]
    selected_idx: number
}

export type BrushFormType = {
    spacing: number
    radius: number
    color: string
    linewidth_profile: string
    pattern: string
    pattern_rotate: boolean
    is_eraser: boolean
}

export const initBrushForm: BrushFormType = {
    spacing: 1,  //turned off by default
    radius: 16,
    color: '#3a3a3a',
    linewidth_profile: null,
    pattern: null,
    pattern_rotate: true,
    is_eraser: false,
}

export const CollageImageShapes: ClipMaskObjectType[] = ['rect', 'circle', 'ellipse', 'polygon', 'bezier', 'text']
export interface CollageLayoutImageType {
    id: ID
    x_rel: number
    y_rel: number
    x_offset: number
    y_offset: number
    scale: number
    width_rel: number
    height_rel: number
    level: number
    priority: number
    shape: ClipMaskObjectType
    padding_px: number
    angle: number
    fit_by: string
    bg_color_hex: string
    alpha: number
    points: string
}

export interface CollageLayoutType {
    id: ID
    name: string
    rel_width: number
    rel_height: number
    project_id: ID
    images: CollageLayoutImageType[]
}

export interface ShapeSet {
    id: ID
    name: string
    license: string
    files: ShapeSetFile[]
}

export interface ShapeSetFile {
    id: ID
    set_id: ID
    filename: string
    mime_type: string
    cz_filename: string
    sk_filename: string
    pl_filename: string
    en_filename: string
    w_h_ratio: number
    tags: string
}

export interface CustomImageSet {
    id: ID
    project_id: ID
    name: string
    files?: CustomImageSetFile[]
}

export interface CustomImageSetFile {
    id: ID
    set_id: ID
    filename: string
    orig_filename: string
    source_url?: string | null
    license?: string | null
    author?: string | null
    uploaded_by?: string | null
    width: number
    height: number
    base?: string
    is_used?: number
}

export type UserType = {
    id: number
    email: string
    first_name: string
    last_name: string
    name: string
    locale: string
    show_newsbox: boolean
    created_at: string
    updated_at: string
}

export type SizeType = {
    width: number
    height: number
}

